import { useEffect, useState } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  SkipForward,
  Loader2,
  Play,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface OutletContext {
  user: any;
  organizationId: string;
}

interface RunStep {
  id: string;
  title_snapshot: string;
  instruction_snapshot: string | null;
  status: string;
  is_critical_snapshot: boolean;
  required_proof_type_snapshot: string;
  due_at: string | null;
  completed_at: string | null;
  assigned_to_user_id: string | null;
}

interface Run {
  id: string;
  name: string;
  status: string;
  started_at: string | null;
  due_at: string | null;
  completed_at: string | null;
  sop_template: {
    title: string;
  };
}

const RunDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user, organizationId } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const [run, setRun] = useState<Run | null>(null);
  const [steps, setSteps] = useState<RunStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (id) {
      fetchRunDetails();
    }
  }, [id]);

  const fetchRunDetails = async () => {
    setLoading(true);
    try {
      const { data: runData, error: runError } = await supabase
        .from("sop_runs")
        .select(`
          *,
          sop_template:sop_templates (title)
        `)
        .eq("id", id)
        .single();

      if (runError) throw runError;
      setRun(runData as any);

      const { data: stepsData, error: stepsError } = await supabase
        .from("sop_run_steps")
        .select("*")
        .eq("sop_run_id", id)
        .order("created_at", { ascending: true });

      if (stepsError) throw stepsError;
      setSteps(stepsData || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const completeStep = async (stepId: string) => {
    setCompleting(stepId);
    try {
      const { error } = await supabase
        .from("sop_run_steps")
        .update({
          status: "COMPLETED",
          completed_at: new Date().toISOString(),
        })
        .eq("id", stepId);

      if (error) throw error;

      // Check if all steps are completed
      const updatedSteps = steps.map((s) =>
        s.id === stepId ? { ...s, status: "COMPLETED", completed_at: new Date().toISOString() } : s
      );
      setSteps(updatedSteps);

      const allCompleted = updatedSteps.every(
        (s) => s.status === "COMPLETED" || s.status === "SKIPPED"
      );

      if (allCompleted) {
        await supabase
          .from("sop_runs")
          .update({
            status: "COMPLETED",
            completed_at: new Date().toISOString(),
          })
          .eq("id", id);

        setRun((prev) =>
          prev ? { ...prev, status: "COMPLETED", completed_at: new Date().toISOString() } : null
        );
      }

      toast({
        title: "Step Completed",
        description: allCompleted ? "Run completed!" : "Step marked as complete.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setCompleting(null);
    }
  };

  const startStep = async (stepId: string) => {
    try {
      const { error } = await supabase
        .from("sop_run_steps")
        .update({
          status: "IN_PROGRESS",
          started_at: new Date().toISOString(),
          assigned_to_user_id: user.id,
        })
        .eq("id", stepId);

      if (error) throw error;

      setSteps((prev) =>
        prev.map((s) =>
          s.id === stepId
            ? { ...s, status: "IN_PROGRESS", assigned_to_user_id: user.id }
            : s
        )
      );

      toast({
        title: "Step Started",
        description: "You are now working on this step.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "IN_PROGRESS":
        return <Clock className="h-5 w-5 text-blue-500" />;
      case "SKIPPED":
        return <SkipForward className="h-5 w-5 text-muted-foreground" />;
      default:
        return <Circle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const completedCount = steps.filter(
    (s) => s.status === "COMPLETED" || s.status === "SKIPPED"
  ).length;
  const progress = steps.length > 0 ? (completedCount / steps.length) * 100 : 0;

  if (loading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="container py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Run not found</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/app/runs")}>
              Back to Runs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-4 sm:py-6 md:py-8 space-y-4 sm:space-y-6 px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/runs")} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">{run.name}</h1>
            <p className="text-sm sm:text-base text-muted-foreground truncate">
              {run.sop_template?.title}
            </p>
          </div>
        </div>
        <Badge
          className={`shrink-0 ml-11 sm:ml-0 w-fit ${
            run.status === "COMPLETED"
              ? "bg-green-500/10 text-green-500"
              : run.status === "IN_PROGRESS"
              ? "bg-blue-500/10 text-blue-500"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {run.status.replace("_", " ")}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {completedCount} of {steps.length} steps completed
            </span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} />
          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
            {run.started_at && (
              <span>Started: {format(new Date(run.started_at), "MMM d, yyyy")}</span>
            )}
            {run.due_at && <span>Due: {format(new Date(run.due_at), "MMM d, yyyy")}</span>}
            {run.completed_at && (
              <span>Completed: {format(new Date(run.completed_at), "MMM d, yyyy")}</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.id}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="mt-0.5 shrink-0">{getStepIcon(step.status)}</div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm sm:text-base">{step.title_snapshot}</span>
                        {step.is_critical_snapshot && (
                          <Badge variant="destructive" className="text-xs">
                            Critical
                          </Badge>
                        )}
                        {step.required_proof_type_snapshot !== "NONE" && (
                          <Badge variant="outline" className="text-xs">
                            Proof: {step.required_proof_type_snapshot}
                          </Badge>
                        )}
                      </div>
                      {step.instruction_snapshot && (
                        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 sm:line-clamp-none">
                          {step.instruction_snapshot}
                        </p>
                      )}
                      {step.due_at && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Due: {format(new Date(step.due_at), "MMM d, h:mm a")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-8 sm:ml-0 shrink-0">
                    {step.status === "NOT_STARTED" && (
                      <Button size="sm" variant="outline" onClick={() => startStep(step.id)}>
                        <Play className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                    )}
                    {step.status === "IN_PROGRESS" && (
                      <Button
                        size="sm"
                        onClick={() => completeStep(step.id)}
                        disabled={completing === step.id}
                      >
                        {completing === step.id ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-1" />
                        )}
                        Complete
                      </Button>
                    )}
                    {step.status === "COMPLETED" && step.completed_at && (
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(step.completed_at), "MMM d, h:mm a")}
                      </span>
                    )}
                  </div>
                </div>
                {index < steps.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RunDetail;
